import type {
  KpiMetricTarget,
  KpiTargetDirection,
  KpiTargetPeriod,
} from '../types';

export const KPI_TARGET_PERIOD_OPTIONS: Array<{
  value: KpiTargetPeriod;
  label: string;
}> = [
  { value: 'absolute', label: 'Absolute' },
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'yearly', label: 'Yearly' },
];

export const KPI_TARGET_DIRECTION_OPTIONS: Array<{
  value: KpiTargetDirection;
  label: string;
}> = [
  { value: 'at_or_above', label: 'At or higher than target' },
  { value: 'at_or_below', label: 'At or lower than target' },
];

export function getKpiTargetPeriodLabel(period: KpiTargetPeriod): string {
  return (
    KPI_TARGET_PERIOD_OPTIONS.find((option) => option.value === period)
      ?.label ?? period
  );
}

export function getKpiTargetDirectionLabel(
  direction: KpiTargetDirection,
): string {
  return (
    KPI_TARGET_DIRECTION_OPTIONS.find((option) => option.value === direction)
      ?.label ?? direction
  );
}

export function parseTargetInput(raw: string): number | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;

  let normalized = trimmed.replace(/[^\d,.-]/g, '');
  if (!normalized) return null;

  const hasComma = normalized.includes(',');
  const hasDot = normalized.includes('.');

  if (hasComma && hasDot) {
    normalized = normalized.replace(/\./g, '').replace(/,/g, '.');
  } else if (hasComma) {
    normalized = normalized.replace(/,/g, '.');
  }

  const value = Number(normalized);
  return Number.isFinite(value) ? value : null;
}

function getPeriodUnitCount(
  period: KpiTargetPeriod,
  dateRange: { from: string; to: string },
): number {
  const start = new Date(dateRange.from);
  const end = new Date(dateRange.to);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return 1;

  const msPerDay = 1000 * 60 * 60 * 24;
  const days = Math.max(
    1,
    Math.floor((end.getTime() - start.getTime()) / msPerDay) + 1,
  );

  switch (period) {
    case 'daily':
      return days;
    case 'weekly':
      return Math.max(days / 7, 1);
    case 'monthly':
      return Math.max(days / 30, 1);
    case 'quarterly':
      return Math.max(days / 91, 1);
    case 'yearly':
      return Math.max(days / 365, 1);
    case 'absolute':
    default:
      return 1;
  }
}

export function getTargetComparisonValue(params: {
  value: number | null;
  target: KpiMetricTarget;
  dateRange: { from: string; to: string };
  normalizeByPeriod: boolean;
}): number | null {
  const { value, target, dateRange, normalizeByPeriod } = params;
  if (value === null || value === undefined) return null;
  if (!normalizeByPeriod || target.period === 'absolute') return value;

  const units = getPeriodUnitCount(target.period, dateRange);
  return units > 0 ? value / units : value;
}

export function isTargetOnTrack(params: {
  value: number | null;
  target: KpiMetricTarget;
  dateRange: { from: string; to: string };
  normalizeByPeriod: boolean;
}): boolean | null {
  const comparisonValue = getTargetComparisonValue(params);
  if (comparisonValue === null) return null;

  return params.target.direction === 'at_or_above'
    ? comparisonValue >= params.target.value
    : comparisonValue <= params.target.value;
}

/**
 * Distanza dal target sulla stessa scala usata da `getTargetComparisonValue`.
 * - `at_or_above`: positivo se manca ancora rispetto al target (target - current).
 * - `at_or_below`: positivo se si è sopra il limite (current - target).
 * Ritorna `null` se non c'è un valore KPI confrontabile.
 */
export function getTargetDistanceFromGoal(params: {
  value: number | null;
  target: KpiMetricTarget;
  dateRange: { from: string; to: string };
  normalizeByPeriod: boolean;
}): number | null {
  const comparisonValue = getTargetComparisonValue(params);
  if (comparisonValue === null) return null;

  if (params.target.direction === 'at_or_above') {
    return params.target.value - comparisonValue;
  }

  return comparisonValue - params.target.value;
}

/**
 * Quanto manca (o di quanto si supera il limite) quando il target NON è soddisfatto.
 * Ritorna sempre un valore positivo, oppure `null` se on-track / senza dati.
 */
export function getTargetOffTrackGapAmount(params: {
  value: number | null;
  target: KpiMetricTarget;
  dateRange: { from: string; to: string };
  normalizeByPeriod: boolean;
}): number | null {
  const onTrack = isTargetOnTrack(params);
  if (onTrack === null || onTrack) return null;

  const distance = getTargetDistanceFromGoal(params);
  if (distance === null) return null;

  const abs = Math.abs(distance);
  if (!Number.isFinite(abs) || abs === 0) return null;
  return abs;
}
